import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { api, apiWithAuth } from '../api';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Grid,
  IconButton,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormHelperText,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox';

const steps = [
  'Basic Information',
  'Business Details',
  'Contact & Social Media',
  'Gallery & Portfolio'
];

const CreateVendorProfile = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    business_name: '',
    contact_info: '',
    bio: '',
    category_id: '',
    location: '',
    website: '',
    email: '',
    phone: '',
    about: '',
    services: '',
    experience_years: '',
    social_media: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
    },
    profile_picture: null,
    cover_photo: null,
    gallery: [],
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [previewImages, setPreviewImages] = useState({
    profile: null,
    cover: null,
    gallery: [],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const fileInputRefs = {
    profile: useRef(null),
    cover: useRef(null),
    gallery: useRef(null),
  };
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: 51.0447, lng: -114.0719 }); // Default to Calgary
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Fetch categories when component mounts
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        let response;
        // Categories endpoint doesn't require auth, but use token if available
        if (token && token.trim()) {
          try {
            response = await apiWithAuth(token).get('/categories/');
          } catch (authError) {
            // If auth fails, try without auth (categories endpoint is public)
            console.warn('Auth failed for categories, trying without auth:', authError);
            response = await api.get('/categories/');
          }
        } else {
          response = await api.get('/categories/');
        }
        setCategories(response.data || []);
        if (response.data && response.data.length === 0) {
          console.warn('Categories list is empty. Categories may need to be initialized in the database.');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        setError('Failed to load categories. Please refresh the page or contact support if the issue persists.');
      }
    };
    fetchCategories();
  }, []);

  // Initialize map
  useEffect(() => {
    if (map.current) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [coordinates.lng, coordinates.lat],
      zoom: 12,
      interactive: false
    });

    // Add marker
    marker.current = new mapboxgl.Marker({
      color: '#1976d2',
      draggable: true
    })
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(map.current);

    // Update coordinates when marker is dragged
    marker.current.on('dragend', () => {
      const lngLat = marker.current.getLngLat();
      setCoordinates({ lat: lngLat.lat, lng: lngLat.lng });
      reverseGeocode(lngLat.lat, lngLat.lng);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update map when coordinates change
  useEffect(() => {
    if (map.current && marker.current) {
      map.current.flyTo({
        center: [coordinates.lng, coordinates.lat],
        zoom: 12
      });
      marker.current.setLngLat([coordinates.lng, coordinates.lat]);
    }
  }, [coordinates]);

  // Debounced search function
  const searchAddress = async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=ca&types=address,place,locality,neighborhood&limit=5`
      );
      const data = await response.json();
      setSuggestions(data.features);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
    }
  };

  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_ACCESS_TOKEN}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setFormData(prev => ({
          ...prev,
          location: data.features[0].place_name
        }));
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  // Handle location input change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      location: value
    }));
    searchAddress(value);
    setShowSuggestions(true);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    const [lng, lat] = suggestion.center;
    setCoordinates({ lat, lng });
    setFormData(prev => ({
      ...prev,
      location: suggestion.place_name
    }));
    setShowSuggestions(false);
  };

  const validateStep = (step) => {
    setValidationAttempted(true);
    switch (step) {
      case 0: // Basic Information
        if (!formData.business_name || !formData.category_id || !formData.location) {
          setError('Please fill in all required fields: Business Name, Category, and Location');
          return false;
        }
        return true;

      case 1: // Business Details
        if (!formData.about || !formData.services || !formData.experience_years) {
          setError('Please fill in all required fields: About Your Business, Services Offered, and Years of Experience');
          return false;
        }
        return true;

      case 2: // Contact & Social Media
        if (!formData.email || !formData.phone) {
          setError('Please fill in all required fields: Email and Phone');
          return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        // Validate phone format (basic validation)
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(formData.phone)) {
          setError('Please enter a valid phone number');
          return false;
        }
        return true;

      case 3: // Gallery & Portfolio
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setError(null);
      setValidationAttempted(false);
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setValidationAttempted(false);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('social_media.')) {
      const platform = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        social_media: {
          ...prev.social_media,
          [platform]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      
      if (type === 'gallery') {
        setFormData(prev => ({
          ...prev,
          gallery: [...prev.gallery, file]
        }));
        setPreviewImages(prev => ({
          ...prev,
          gallery: [...prev.gallery, previewUrl]
        }));
      } else {
        // For profile picture and cover photo
        setFormData(prev => ({
          ...prev,
          [type]: file
        }));
        setPreviewImages(prev => ({
          ...prev,
          [type === 'profile_picture' ? 'profile' : 'cover']: previewUrl
        }));
      }
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    setPreviewImages(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate the current step before submitting
    if (!validateStep(activeStep)) {
      return;
    }

    // If not on the last step, proceed to next step instead of submitting
    if (activeStep < steps.length - 1) {
      handleNext();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const userResponse = await apiWithAuth(token).get('/auth/me');
      const userId = userResponse.data.id;

      // Create FormData for file uploads
      const formDataToSend = new FormData();
      
      // Add all non-file fields
      formDataToSend.append('business_name', formData.business_name);
      formDataToSend.append('contact_info', formData.contact_info);
      formDataToSend.append('bio', formData.bio || '');
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('website', formData.website || '');
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('about', formData.about);
      formDataToSend.append('services', formData.services);
      formDataToSend.append('experience_years', formData.experience_years);
      
      // Add social media data as JSON string
      if (formData.social_media) {
        formDataToSend.append('social_media', JSON.stringify(formData.social_media));
      }

      // Add files
      if (formData.profile_picture) {
        formDataToSend.append('profile_picture', formData.profile_picture);
      }
      if (formData.cover_photo) {
        formDataToSend.append('cover_photo', formData.cover_photo);
      }
      if (formData.gallery && formData.gallery.length > 0) {
        formData.gallery.forEach((file) => {
          formDataToSend.append('gallery', file);
        });
      }

      try {
        const response = await apiWithAuth(token).post('/vendors/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // If we get here, the request was successful
        // Invalidate vendor profile query to refresh the cache
        queryClient.invalidateQueries(['vendorProfile']);
        setSnackbar({
          open: true,
          message: 'Profile created successfully!',
          severity: 'success'
        });
        setTimeout(() => {
          history.push('/dashboard');
        }, 1500);
      } catch (error) {
        // If we get a network error but the profile was created, show success
        if (error.message === 'Network Error') {
          setSnackbar({
            open: true,
            message: 'Profile created successfully!',
            severity: 'success'
          });
          setTimeout(() => {
            history.push('/dashboard');
          }, 1500);
        } else {
          throw error; // Re-throw other errors
        }
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        history.push('/login');
      } else if (error.response?.status === 400) {
        setError(error.response?.data?.detail || 'Invalid data provided. Please check your inputs.');
      } else {
        setError(error.response?.data?.detail || 'Failed to create profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Add cleanup function for preview URLs
  React.useEffect(() => {
    return () => {
      // Cleanup preview URLs when component unmounts
      if (previewImages.profile) URL.revokeObjectURL(previewImages.profile);
      if (previewImages.cover) URL.revokeObjectURL(previewImages.cover);
      previewImages.gallery.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewImages]);

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Profile Picture
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src={previewImages.profile}
                  sx={{ 
                    width: 100, 
                    height: 100,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    '& img': {
                      objectFit: 'cover'
                    }
                  }}
                />
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRefs.profile.current.click()}
                    sx={{ mb: 1 }}
                  >
                    {previewImages.profile ? 'Change Profile Picture' : 'Upload Profile Picture'}
                  </Button>
                  {previewImages.profile && (
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, profile_picture: null }));
                        setPreviewImages(prev => ({ ...prev, profile: null }));
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRefs.profile}
                  onChange={(e) => handleFileChange('profile_picture', e)}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                required
                error={validationAttempted && !formData.business_name}
                helperText={validationAttempted && !formData.business_name ? 'Business name is required' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={validationAttempted && !formData.category_id}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.category_id} value={category.category_id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationAttempted && !formData.category_id && (
                  <FormHelperText>Category is required</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleLocationChange}
                  required
                  error={validationAttempted && !formData.location}
                  helperText={validationAttempted && !formData.location ? 'Location is required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 200,
                      overflow: 'auto',
                      mt: 1,
                      boxShadow: 3
                    }}
                  >
                    <List>
                      {suggestions.map((suggestion) => (
                        <ListItem
                          key={suggestion.id}
                          button
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          <ListItemIcon>
                            <LocationIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={suggestion.text}
                            secondary={suggestion.place_name}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                ref={mapContainer}
                sx={{
                  height: 300,
                  width: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="About Your Business"
                name="about"
                value={formData.about}
                onChange={handleChange}
                multiline
                rows={4}
                required
                error={validationAttempted && !formData.about}
                helperText={validationAttempted && !formData.about ? 'About your business is required' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Services Offered"
                name="services"
                value={formData.services}
                onChange={handleChange}
                multiline
                rows={3}
                required
                error={validationAttempted && !formData.services}
                helperText={validationAttempted && !formData.services ? 'Services offered is required' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Years of Experience"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleChange}
                type="number"
                required
                error={validationAttempted && !formData.experience_years}
                helperText={validationAttempted && !formData.experience_years ? 'Years of experience is required' : ''}
                InputProps={{
                  endAdornment: <InputAdornment position="end">years</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebsiteIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                error={validationAttempted && !formData.email}
                helperText={validationAttempted && !formData.email ? 'Email is required' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                error={validationAttempted && !formData.phone}
                helperText={validationAttempted && !formData.phone ? 'Phone number is required' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Social Media Links
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    name="social_media.facebook"
                    value={formData.social_media.facebook}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FacebookIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    name="social_media.instagram"
                    value={formData.social_media.instagram}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InstagramIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Twitter"
                    name="social_media.twitter"
                    value={formData.social_media.twitter}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TwitterIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="LinkedIn"
                    name="social_media.linkedin"
                    value={formData.social_media.linkedin}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedInIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Cover Photo
              </Typography>
              <Box sx={{ position: 'relative', height: 200, mb: 3 }}>
                {previewImages.cover ? (
                  <img
                    src={previewImages.cover}
                    alt="Cover preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <Paper
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 8,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => fileInputRefs.cover.current.click()}
                    >
                      Upload Cover Photo
                    </Button>
                  </Paper>
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRefs.cover}
                  onChange={(e) => handleFileChange('cover_photo', e)}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Gallery
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddPhotoIcon />}
                  onClick={() => fileInputRefs.gallery.current.click()}
                >
                  Add Photos
                </Button>
              </Box>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRefs.gallery}
                onChange={(e) => handleFileChange('gallery', e)}
              />
              <Grid container spacing={2}>
                {previewImages.gallery.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ position: 'relative' }}>
                      <img
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                        }}
                        onClick={() => handleRemoveGalleryImage(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Set Up Your Vendor Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph align="center" sx={{ mb: 4 }}>
            Create a compelling profile to showcase your business to event organizers
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleSubmit}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                type="button"
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Profile'}
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                >
                  Next
                </Button>
              )}
            </Box>
          </form>
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
      />
    </Container>
  );
};

export default CreateVendorProfile; 